
import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from 'react-i18next';

interface LLMSContentProps {
  content: string;
  isEditing: boolean;
  onEdit: (newContent: string) => void;
  onToggleEdit: () => void;
}

export const LLMSContent: React.FC<LLMSContentProps> = ({
  content,
  isEditing,
  onEdit,
  onToggleEdit,
}) => {
  const { t } = useTranslation();
  
  const handleDownload = () => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'llms.txt';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    toast.success(t('analysis.fileDownloaded'));
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    toast.success(t('analysis.contentCopied'));
  };

  return (
    <Card className="p-6">
      <div className="flex items-center space-x-2">
        <FileText className="h-5 w-5 text-blue-500" />
        <h2 className="text-xl font-semibold">{t('analysis.llmsContent')}</h2>
      </div>
      <div className="mt-4">
        {isEditing ? (
          <textarea
            value={content}
            onChange={(e) => onEdit(e.target.value)}
            className="w-full h-[400px] p-4 font-mono text-sm border rounded-lg"
          />
        ) : (
          <pre className="w-full h-[400px] p-4 font-mono text-sm bg-muted rounded-lg overflow-auto">
            {content}
          </pre>
        )}
      </div>
      <div className="mt-4 space-x-2">
        <Button 
          variant="outline" 
          size="sm"
          onClick={onToggleEdit}
        >
          {isEditing ? t('analysis.preview') : t('analysis.edit')}
        </Button>
        <Button 
          variant="outline" 
          size="sm"
          onClick={handleCopy}
        >
          {t('analysis.copy')}
        </Button>
        <Button 
          variant="outline"
          size="sm"
          onClick={handleDownload}
        >
          {t('analysis.download')}
        </Button>
      </div>
    </Card>
  );
};
